import { PROBLEM_STATUS, type ProblemCode } from '@socialfy/api-contract';
import { de } from './de.js';
import { format } from './format.js';
import { t } from './t.js';

/** Parameter einer Erinnerung wie im Vertrag: nur kurze Texte, Zahlen oder `null` ([A] 4.9). */
export type ReminderParams = Readonly<Record<string, string | number | null>>;

function numberParam(params: ReminderParams, key: string): number | null {
  const value = params[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** „heute“ bzw. „in 3 Tagen“; ohne Angabe „bald“. */
function whenDays(days: number | null): string {
  if (days === null) {
    return 'bald';
  }
  return days <= 0
    ? de.reminder.today
    : t('reminder.inDuration', { duration: format.daysDative(days) });
}

/** Zeitraum aus Stunden: ab zwei Tagen in Tagen, sonst in Stunden. */
function durationOfHours(hours: number | null): string {
  if (hours === null) {
    return 'einiger Zeit';
  }
  return hours >= 48 ? format.daysDative(Math.floor(hours / 24)) : format.hoursDative(hours);
}

function gapReason(reason: string | number | null | undefined): string {
  return typeof reason === 'string' && reason in de.dataGapReason && reason !== 'unknown'
    ? de.dataGapReason[reason as keyof typeof de.dataGapReason]
    : de.dataGapReason.unknown;
}

/**
 * Klartext einer Erinnerung. Der Server liefert keine Texte, nur Art und Parameter (Vertrag `Reminder`);
 * unbekannte Arten fallen auf einen allgemeinen Hinweis zurück, damit neue Arten nie die Oberfläche brechen.
 */
export function reminderText(kind: string, params: ReminderParams): string {
  switch (kind) {
    case 'token_expiring':
      return t('reminder.token_expiring', { when: whenDays(numberParam(params, 'days_left')) });
    case 'consent_expiring':
      return t('reminder.consent_expiring', { when: whenDays(numberParam(params, 'days_left')) });
    case 'token_expired':
    case 'token_issue_unknown':
    case 'auth_failed':
    case 'consent_revoked':
    case 'account_mismatch':
    case 'identity_review':
    case 'self_url_unverified':
    case 'art14_due':
    case 'restore_applied':
    case 'event_delivery_failed':
      return de.reminder[kind];
    case 'unbound_token':
      return t('reminder.unbound_token', {
        duration: durationOfHours(numberParam(params, 'hours_unbound')),
      });
    case 'sync_stale':
      return t('reminder.sync_stale', {
        duration: durationOfHours(numberParam(params, 'hours_since')),
      });
    case 'data_gap':
      return t('reminder.data_gap', { reason: gapReason(params['reason']) });
    case 'processing_errors':
      return t('reminder.processing_errors', {
        count: format.number(numberParam(params, 'count') ?? 0),
      });
    case 'privacy_request_due': {
      const left = numberParam(params, 'days_left');
      return left !== null && left < 0
        ? de.reminder.privacy_request_overdue
        : t('reminder.privacy_request_due', { when: whenDays(left) });
    }
    case 'erasure_pending':
      return t('reminder.erasure_pending', {
        duration: durationOfHours(numberParam(params, 'hours_pending')),
      });
    default:
      return de.reminder.unknown;
  }
}

/** Knopftext zur Erinnerung: erneuern beim Zugang, sonst „Ansehen“. */
export function reminderAction(kind: string): string {
  return kind === 'token_expiring' || kind === 'token_expired' || kind === 'auth_failed'
    ? de.reminder.renew
    : de.reminder.view;
}

const isProblemCode = (code: string): code is ProblemCode => code in PROBLEM_STATUS;

export type ProblemTextOptions = {
  /** Modulspezifischer Grund der Antwort (`Problem.reason`). */
  reason?: string | undefined;
  /** Uhrzeit, ab der eine erreichte Grenze wieder frei ist (aus `Retry-After`, schon formatiert). */
  retryAt?: string | undefined;
};

/** Klartext zu einem Fehlercode; unbekannte Codes zeigen den allgemeinen Text. */
export function problemText(code: string, options: ProblemTextOptions = {}): string {
  if (code === 'rate_limited' && options.reason === 'limit_reached') {
    return options.retryAt === undefined
      ? de.problem.limitReachedLater
      : t('problem.limitReached', { time: options.retryAt });
  }
  // Sperrt nur die Demo die Handlung (Domäne: `action_disabled` mit Grund `demo_read_only`), ist nicht „ausgeschaltet“
  if (code === 'action_disabled' && options.reason === 'demo_read_only') {
    return de.problem.demo_read_only;
  }
  return isProblemCode(code) ? de.problem[code] : de.problem.internal;
}
